import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  ArrowPathIcon,
  CpuChipIcon,
  EyeIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';

const AgentList = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.getAgents();
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  // Filtered agents based on search and status
  const filteredAgents = useMemo(() => {
    let filtered = agents;

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(agent => 
        agent.name.toLowerCase().includes(search) ||
        (agent.description && agent.description.toLowerCase().includes(search)) ||
        agent.model.toLowerCase().includes(search) ||
        (agent.created_by_name && agent.created_by_name.toLowerCase().includes(search))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(agent => agent.status === statusFilter);
    }

    return filtered;
  }, [agents, searchTerm, statusFilter]);

  // Get unique statuses for filter options
  const uniqueStatuses = useMemo(() => {
    const statuses = [...new Set(agents.map(agent => agent.status))];
    return statuses.filter(Boolean);
  }, [agents]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      training: 'bg-yellow-100 text-yellow-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleDeleteAgent = async (agentId, agentName) => {
    if (!window.confirm(`Are you sure you want to delete agent "${agentName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(agentId);
      await api.deleteAgent(agentId);
      toast.success(`Agent "${agentName}" deleted successfully`);
      
      // Refresh the agents list
      fetchAgents();
    } catch (error) {
      console.error('Error deleting agent:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete agent';
      toast.error(errorMessage);
    } finally {
      setDeleting(null);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredAgents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAgents = filteredAgents.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Training</h1>
          <p className="text-gray-600 mt-1">
            Train your AI agents with custom instructions and knowledge bases
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchAgents}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link
            to="/dashboard/agents/train"
            // to="/dashboard/agents/create"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Train Agent
          </Link>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search agents by name, description, model, or creator..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium transition-colors ${
              showFilters || statusFilter !== 'all'
                ? 'border-blue-500 text-blue-700 bg-blue-50'
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
            {statusFilter !== 'all' && (
              <span className="ml-2 bg-blue-500 text-white text-xs rounded-full px-2 py-0.5">
                1
              </span>
            )}
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Statuses</option>
                  {uniqueStatuses.map(status => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            {(searchTerm || statusFilter !== 'all') && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Results count */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {startIndex + 1} - {Math.min(startIndex + itemsPerPage, filteredAgents.length)} of {filteredAgents.length} agents
        </div>
      </div>

      {/* Agents List */}
      {filteredAgents.length === 0 && agents.length > 0 ? (
        <div className="text-center py-12">
          <CpuChipIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No agents found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Try adjusting your search terms or filters.
          </p>
        </div>
      ) : filteredAgents.length === 0 ? (
        <div className="text-center py-12">
          <CpuChipIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No agent training found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by training your first AI agent.
          </p>
          <div className="mt-6">
            <Link
              to="/dashboard/agents/train"
              // to="/dashboard/agents/create"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Train Agent
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {paginatedAgents.map((agent) => (
                <li key={agent.id}>
                  <div className="px-4 py-4 hover:bg-gray-50">
                    {/* Mobile: Vertical layout */}
                    <div className="flex flex-col space-y-3">
                      {/* Agent Header - Icon, Name, Status */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <CpuChipIcon className="h-6 w-6 text-blue-600" />
                            </div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center space-x-2 flex-wrap">
                              <h3 className="text-base font-medium text-gray-900 truncate">
                                {agent.name}
                              </h3>
                              {getStatusBadge(agent.status)}
                            </div>
                            <div className="mt-1 text-sm text-gray-500">
                              Model: {agent.model} • Created: {formatDate(agent.created_at)}
                            </div>
                          </div>
                        </div>
                        
                        {/* Action Buttons - Always visible on mobile */}
                        <div className="flex items-center space-x-1 ml-2">
                          <Link
                            to={`/dashboard/agents/${agent.id}`}
                            className="inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 min-w-0"
                          >
                            <EyeIcon className="h-4 w-4" />
                            <span className="hidden sm:inline ml-1">View</span>
                          </Link>
                          <button
                            onClick={() => handleDeleteAgent(agent.id, agent.name)}
                            disabled={deleting === agent.id}
                            className="inline-flex items-center px-2 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 min-w-0"
                          >
                            <TrashIcon className="h-4 w-4" />
                            <span className="hidden sm:inline ml-1">{deleting === agent.id ? 'Deleting...' : 'Delete'}</span>
                          </button>
                        </div>
                      </div>

                      {/* Documents Info */}
                      {agent.document_count > 0 && (
                        <div className="flex items-center text-sm text-gray-500">
                          <DocumentTextIcon className="h-4 w-4 mr-1" />
                          {agent.document_count} documents
                        </div>
                      )}

                      {/* Stats Footer */}
                      <div className="flex items-center justify-between text-xs text-gray-400 border-t pt-2">
                        <div className="flex items-center space-x-2">
                          <span>{agent.total_conversations || 0} conversations</span>
                          <span>•</span>
                          <span>{agent.total_messages_processed || 0} messages</span>
                        </div>
                        <span>Created by {agent.created_by_name}</span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Pagination Controls */}
          {filteredAgents.length > itemsPerPage && (
            <div className="bg-white px-6 py-4 border-t border-gray-200 sm:rounded-b-md">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(startIndex + itemsPerPage, filteredAgents.length)}
                  </span>{' '}
                  of <span className="font-medium">{filteredAgents.length}</span> results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Previous
                  </button>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`inline-flex items-center px-3 py-1 border text-sm font-medium rounded-md ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    {totalPages > 5 && (
                      <span className="inline-flex items-center px-2 py-1 text-sm text-gray-500">
                        ...
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Next
                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Stats Footer */}
      {filteredAgents.length > 0 && (
        <div className="bg-white px-6 py-4 border-t border-gray-200 sm:rounded-b-md">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>Total: {filteredAgents.length} agent{filteredAgents.length !== 1 ? 's' : ''}</span>
            <span>
              Active: {filteredAgents.filter(a => a.status === 'active').length} | 
              Inactive: {filteredAgents.filter(a => a.status === 'inactive').length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentList;